/* eslint-disable @typescript-eslint/ban-ts-ignore */
import BaseScene, { BaseSceneConfig } from "../../scenes/BaseScene";

import GlitchSource from "../../../assets/shader/glitch.glsl";
import BloomSource from "../../../assets/shader/bloom.glsl";
import DigitalScan from "../../../assets/shader/digital-scan.glsl";
import Blur from "../../../assets/shader/blur.glsl";
import Mask from "../../../assets/shader/mask.glsl";
import MiniGame from "../../../assets/shader/mini-game.glsl";
import MainGame from "../../../assets/shader/main-post.glsl";
import VertFlipped from "../../../assets/shader/flipped.vert";
import SpongePlugin from "./SpongePlugin";

export class PostPipeline extends Phaser.Renderer.WebGL.Pipelines
  .TextureTintPipeline {
  private _time: number = 0;
  private _effectProgress: number = 0;
  private _transitionType: number = 0;
  private _resolution: { x: number; y: number } = { x: 0, y: 0 };

  set time(i: number) {
    this._time = i;
    this.setFloat1("time", this._time + 100);
  }

  get time(): number {
    return this._time;
  }

  set res(i: { x: number; y: number }) {
    this._resolution = i;
    this.setFloat2("resolution", this._resolution.x, this._resolution.y);
  }

  get res(): { x: number; y: number } {
    return this._resolution;
  }

  set effectProgress(i: number) {
    this._effectProgress = i;
    this.setFloat1("effectProgress", this._effectProgress);
  }

  get effectProgress(): number {
    return this._effectProgress;
  }
  set transitionType(i: number) {
    this._transitionType = i;
    this.setFloat1("transitionType", this._transitionType);
  }

  get transitionType(): number {
    return this._transitionType;
  }

  tween: Phaser.Tweens.Tween;

  fire(scene: Phaser.Scene, duration: number, intensity: number = 1) {
    if (this.tween) {
      this.tween.stop();
    }

    this.effectProgress = intensity;
    scene.add.tween({
      targets: this,
      duration,
      effectProgress: 0,
      ease: "Quad.easeOut",
    });
  }

  constructor(
    game: Phaser.Game,
    source: string,
    id: string,
    flipped: boolean = false
  ) {
    super({
      game: game,
      renderer: game.renderer,
      fragShader: source,
      vertShader: flipped ? VertFlipped : undefined,
    });

    (game.renderer as Phaser.Renderer.WebGL.WebGLRenderer).addPipeline(
      id,
      this
    );
  }
}

/**
 * a collection of plugins all accessible via scene.sponge (when extending base scene.)
 * @category plugins
 * @export
 * @class SpongePlugin
 * @extends {Phaser.Plugins.BasePlugin}
 */

export interface CameraConfig {
  sceneKey: string;
}

export default class PostEffectPlugin extends Phaser.Plugins.BasePlugin {
  pipelines: CameraConfig[];

  sponge: SpongePlugin;

  /**
   * a reference to our game.
   *
   * @type {Phaser.Game}
   * @memberof Sponge
   */
  public game: Phaser.Game;

  constructor(pluginManager: Phaser.Plugins.PluginManager) {
    super(pluginManager);
    this.game = pluginManager.game;

    
   
  }

  startUp(sponge: SpongePlugin){
     this.sponge = sponge;


     if(this.sponge.data.save.lowPerFormanceMode){
      return;
    }

    this.freshPipe(MainGame, "main-game-post", false);

    this.freshPipe(MiniGame, "mini-game-post", false);

    const blurPipline = this.freshPipe(Blur, "blur", false);

    blurPipline.setFloat1("radius", 1.0);
    blurPipline.setFloat2("dir", 1.0, 1.0);
  }

  freshPipe(src: string, id: string, fliped: boolean = false): PostPipeline {
    const pipe = new PostPipeline(this.game, src, id, fliped);

    pipe.res = {
      x: this.game.config.width as number,
      y: this.game.config.height as number,
    };

    pipe.time = 0;
    pipe.effectProgress = 0;
    pipe.transitionType = 0;

    this.game.events.on("step", (time: number, delta: number) => {
      pipe.time = time / 1000;
    });

    return pipe;
  }

  addMiniGamePost(object: BaseScene): boolean {

    if(this.sponge.data.save.lowPerFormanceMode){
      return false;
    }

    try {
    object.cameras.main.setRenderToTexture("mini-game-post");
    } catch(e){
      console.warn(e);
      return false;
    }

    return true;
  }

  addMainGamePost(object: BaseScene): boolean {

    if(this.sponge.data.save.lowPerFormanceMode){
      return false;
    }
    try {

    object.cameras.main.setRenderToTexture("main-game-post");
  } catch(e){
    console.warn(e);
    return false;
  }

  return true;
  
  }
  addBlur(
    object: BaseScene | Phaser.GameObjects.Sprite | Phaser.GameObjects.Image
  ): boolean {

    if(this.sponge.data.save.lowPerFormanceMode){
      return false;
    }

    try {

    //@ts-ignore
    if (object.cameras) {
      //@ts-ignore
      object.cameras.main.setRenderToTexture("blur");
    } else {
      //@ts-ignore
      object.setPipeline("blur");
    }
  } catch(e){
    console.warn(e);
    return false;
  }

  return true;

  }


  addGlitch(
    object: BaseScene | Phaser.GameObjects.Sprite | Phaser.GameObjects.Image,
    scene?: BaseScene
  ): boolean {

    if(this.sponge.data.save.lowPerFormanceMode){
      return false;
    }

    try {

    this.freshPipe(GlitchSource, "glitch", true);
    if ((object as BaseScene).cameras) {
      (object as BaseScene).cameras.main.setRenderToTexture("glitch", true);
    } else {
      (object as Phaser.GameObjects.Sprite).setPipeline("glitch");
      console.log((object as Phaser.GameObjects.Sprite).pipeline);
    }
  } catch(e){
    console.warn(e);
    return false;
  }

  return true;

  }
}
